import Icon from "@/components/feature/Icon";
import { useSiteSettings } from "@/lib/siteSettings";

interface SocialCard {
  name: string;
  handle: string;
  description: string;
  icon: string;
  color: string;
  /** null = 官方帳號尚未開通，畫面顯示「建置中」 */
  url: string | null;
}

export default function SocialMediaSection() {
  const { settings } = useSiteSettings();
  const { contact, social } = settings;

  const socialLinks: SocialCard[] = [
    {
      name: "LINE 社群",
      handle: contact.community_url ? "TAIAA 協會社群" : "官方帳號建置中",
      description: "加入協會 LINE 社群，即時接收活動通知、交流訊息與最新動態",
      icon: "ri-chat-1-fill",
      color: "bg-[#06C755]",
      url: contact.community_url || null,
    },
    {
      name: "Facebook",
      handle: social.facebook_url ? "TAIAA 粉絲專頁" : "官方帳號建置中",
      description: social.facebook_url ? "追蹤協會粉絲專頁，掌握活動與產業資訊" : "協會粉絲專頁籌備中，開通後將於此公布",
      icon: "ri-facebook-circle-fill",
      color: "bg-[#1877F2]",
      url: social.facebook_url || null,
    },
    {
      name: "LinkedIn",
      handle: social.linkedin_url ? "TAIAA 專頁" : "官方帳號建置中",
      description: social.linkedin_url ? "在 LinkedIn 追蹤協會的產業合作與專業動態" : "協會 LinkedIn 專頁籌備中，開通後將於此公布",
      icon: "ri-linkedin-box-fill",
      color: "bg-[#0A66C2]",
      url: social.linkedin_url || null,
    },
    {
      name: "YouTube",
      handle: social.youtube_url ? "TAIAA 頻道" : "官方帳號建置中",
      description: social.youtube_url ? "觀看論壇、講座與課程精華影片" : "協會影音頻道籌備中，開通後將於此公布",
      icon: "ri-youtube-fill",
      color: "bg-[#FF0000]",
      url: social.youtube_url || null,
    },
  ];

  return (
    <section id="social-media" className="py-16 md:py-24 bg-navy-950 relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="none">
          <defs>
            <pattern id="social-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="800" height="400" fill="url(#social-grid)" />
        </svg>
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-sm font-medium tracking-[0.2em] text-silver-400 uppercase mb-3">
            Follow Us
          </p>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4">
            社群媒體與頻道
          </h2>
          <p className="text-silver-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            追蹤我們的社群帳號，即時掌握 AI 產業動態、活動資訊與專業洞察。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
          {socialLinks.map((link) =>
            link.url ? (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-white/5 border border-white/10 rounded-xl p-5 md:p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl ${link.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className={`${link.icon} text-white text-xl`} />
                  </div>
                  <Icon className="ri-external-link-line text-white/30 text-sm group-hover:text-white/60 transition-colors" />
                </div>

                <h3 className="text-white font-semibold text-base mb-1">
                  {link.name}
                </h3>
                <p className="text-silver-400 text-sm mb-3">{link.handle}</p>
                <p className="text-silver-500 text-sm leading-relaxed">
                  {link.description}
                </p>
              </a>
            ) : (
              <div
                key={link.name}
                aria-disabled="true"
                className="relative bg-white/5 border border-white/10 rounded-xl p-5 md:p-6 opacity-60"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl ${link.color} flex items-center justify-center`}
                  >
                    <Icon className={`${link.icon} text-white text-xl`} />
                  </div>
                  <span className="text-[11px] font-medium tracking-wider text-silver-300 border border-white/20 rounded-full px-2 py-0.5">
                    建置中
                  </span>
                </div>

                <h3 className="text-white font-semibold text-base mb-1">
                  {link.name}
                </h3>
                <p className="text-silver-400 text-sm mb-3">{link.handle}</p>
                <p className="text-silver-500 text-sm leading-relaxed">
                  {link.description}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
}
